import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getContent, getLegalDoc, getDeckCardMeanings, SiteContent, ServiceContent, DeckContent, LegalDocContent, CardMeaning } from "../api/content";
import { getActiveLang } from "../i18n";

export const contentQueryKey = (lang: string) => ["content", lang] as const;

export function useContent(): UseQueryResult<SiteContent> {
  // Subscribe to react-i18next so the hook re-renders on language change.
  useTranslation();
  const lang = getActiveLang();
  return useQuery({
    queryKey: contentQueryKey(lang),
    queryFn: () => getContent(lang),
  });
}

/** A text lookup that returns "" for a missing key (never throws). */
export function text(content: SiteContent | undefined, key: string): string {
  return content?.texts?.[key] ?? "";
}

/** A link lookup that returns "" for a missing key. */
export function link(content: SiteContent | undefined, key: string): string {
  return content?.links?.[key] ?? "";
}

/** Returns the admin-set AUD→USD rate; 0 means "hide the USD subtitle". */
export function usdRate(content: SiteContent | undefined): number {
  return content?.usdRate ?? 0;
}

/** Service by stable key — undefined if absent. */
export function service(
  content: SiteContent | undefined,
  key: string,
): ServiceContent | undefined {
  return content?.services?.find((s) => s.key === key);
}

/** Deck by stable key — undefined if absent. */
export function deck(
  content: SiteContent | undefined,
  key: string,
): DeckContent | undefined {
  return content?.decks?.find((d) => d.key === key);
}

export function useLegalDoc(slug: string): UseQueryResult<LegalDocContent> {
  // Subscribe to react-i18next so the doc re-fetches on language change.
  useTranslation();
  const lang = getActiveLang();
  return useQuery({
    queryKey: ["legal", slug, lang],
    queryFn: () => getLegalDoc(slug, lang),
  });
}

export const cardMeaningsQueryKey = (deck: string, lang: string) =>
  ["card-meanings", deck, lang] as const;

export function useCardMeanings(deck: string): UseQueryResult<CardMeaning[]> {
  useTranslation();
  const lang = getActiveLang();
  return useQuery({
    queryKey: cardMeaningsQueryKey(deck, lang),
    queryFn: () => getDeckCardMeanings(deck, lang),
  });
}
